/**
 * app/screens/SettingsScreen.tsx
 * -------------------------------
 * What: Settings hub organising cards for account details, display preferences,
 * reporting, legal compliance, and developer tools.
 * Why: Centralises configurable features and entry points to utility flows.
 */
import React, { useState, useCallback } from 'react';
import { SafeAreaView, StatusBar, ScrollView, View, StyleSheet, TouchableOpacity, Text, Platform } from 'react-native';
import { useAuth } from '../../lib/AuthContext';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../lib/ThemeContext';
import { useConfig } from '../../lib/ConfigContext';

// External Cards
import SubscriptionStatusCardExternal from './settings/cards/SubscriptionStatusCard';
import DevToolsCardExternal from './settings/cards/DevToolsCard';
import ReportingCardExternal from './settings/cards/ReportingCard';
import MyDetailsCardExternal from './settings/cards/MyDetailsCard';
import ContactCardExternal from './settings/cards/ContactCard';
import DisplayPreferencesCardExternal from './settings/cards/DisplayPreferencesCard';
import LegalComplianceCardExternal from './settings/cards/LegalComplianceCard';

type CardKey =
  | 'myDetails'
  | 'subscriptionStatus'
  | 'reporting'
  | 'displayPreferences'
  | 'inAppContact'
  | 'legalCompliance'
  | 'devTools';

export default function SettingsScreen() {
  const { signOut } = useAuth();
  const { theme, themeType } = useTheme();
  const { config } = useConfig();

  const [expandedCard, setExpandedCard] = useState<CardKey | null>(null);

  const toggleCard = useCallback((key: CardKey) => {
    setExpandedCard((prev) => (prev === key ? null : key));
  }, []);

  const settingsFlags = config.SettingsScreen ?? {};

  const isCardEnabled = (flag: any): boolean =>
    flag === undefined ? true : typeof flag === 'object' ? flag.enabled !== false : flag !== false;

  const getCardOrder = (flag: any, defaultOrder: number): number =>
    flag && typeof flag === 'object' && typeof flag.order === 'number' ? flag.order : defaultOrder;

  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};

  const orderedCards = [
    { key: 'myDetails', order: getCardOrder(settingsFlags.myDetails, 1), enabled: isCardEnabled(settingsFlags.myDetails), render: () => (
      <MyDetailsCardExternal expanded={expandedCard === 'myDetails'} onToggle={() => toggleCard('myDetails')} />
    )},
    { key: 'subscriptionStatus', order: getCardOrder(settingsFlags.subscriptionStatus, 2), enabled: isCardEnabled(settingsFlags.subscriptionStatus), render: () => (
      <SubscriptionStatusCardExternal expanded={expandedCard === 'subscriptionStatus'} onToggle={() => toggleCard('subscriptionStatus')} />
    )},
    { key: 'reporting', order: getCardOrder(settingsFlags.reporting, 3), enabled: isCardEnabled(settingsFlags.reporting), render: () => (
      <ReportingCardExternal expanded={expandedCard === 'reporting'} onToggle={() => toggleCard('reporting')} />
    )},
    { key: 'displayPreferences', order: getCardOrder(settingsFlags.displayPreferences, 4), enabled: isCardEnabled(settingsFlags.displayPreferences), render: () => (
      <DisplayPreferencesCardExternal expanded={expandedCard === 'displayPreferences'} onToggle={() => toggleCard('displayPreferences')} />
    )},
    { key: 'inAppContact', order: getCardOrder(settingsFlags.inAppContact, 5), enabled: isCardEnabled(settingsFlags.inAppContact), render: () => (
      <ContactCardExternal expanded={expandedCard === 'inAppContact'} onToggle={() => toggleCard('inAppContact')} />
    )},
    { key: 'legalCompliance', order: getCardOrder(settingsFlags.legalCompliance, 6), enabled: isCardEnabled(settingsFlags.legalCompliance), render: () => (
      <LegalComplianceCardExternal expanded={expandedCard === 'legalCompliance'} onToggle={() => toggleCard('legalCompliance')} />
    )},
    { key: 'devTools', order: getCardOrder(settingsFlags.devTools, 7), enabled: isCardEnabled(settingsFlags.devTools), render: () => (
      <DevToolsCardExternal expanded={expandedCard === 'devTools'} onToggle={() => toggleCard('devTools')} />
    )},
  ].filter(c => c.enabled).sort((a, b) => a.order - b.order);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle={themeType === 'dark' || themeType === 'blueNeon' ? 'light-content' : 'dark-content'} />
      <ScrollView contentContainerStyle={[styles.scrollContent, { paddingHorizontal: 16 }]}>
        <View style={styles.content}>
          {orderedCards.map(c => (
            <React.Fragment key={c.key}>{c.render()}</React.Fragment>
          ))}
          <View style={[styles.signOutContainer, { backgroundColor: theme.card, borderColor: theme.border, borderWidth: 1 }, glowStyle]}>
            <TouchableOpacity
              style={[styles.button, styles.signOutButton]}
              onPress={signOut}
            >
              <Ionicons name="log-out-outline" size={24} color="#ff3b30" />
              <Text style={[styles.buttonText, { color: '#ff3b30' }]}>Sign Out</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  content: { paddingVertical: 20, gap: 16 },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  signOutContainer: {
    padding: 16,
    borderRadius: 8,
  },
  signOutButton: { width: '100%' },
});
